/* eslint-disable @typescript-eslint/no-explicit-any */
 
 
 
 
 
 
import { create } from 'zustand';
import { conditionalDevtools } from './middleware/conditionalDevtools';
import { createSyncMiddleware, WithSyncControls } from './middleware/syncMiddleware';
import { LogoConfig } from '@smartphone-tycoon/shared-3d';
import { ResearchIntegrationService } from '../services/ResearchIntegrationService';
import { 
  components as baseComponents, 
  materialOptions as baseMaterialOptions,
  getDefaultComponents 
} from '@smartphone-tycoon/shared-core';
import type { ResearchUnlock, ComponentCategory } from '@smartphone-tycoon/shared-core';

export interface DesignConfiguration {
  name: string;
  price: number;
  color: string;
  material: string;
  buttonColor: string;
  hasButtons: boolean;
  screenBorderColor: string;
  cameraStyle: string;
  cameraColor: string;
  cameraPosition: { x: number; y: number };
  buttonPositions: { volume: number; power: number };
  phoneSize: { width: number; height: number; depth: number };
  screenSize: { width: number; height: number };
  cornerRadius: number;
  cpu: string;
  gpu: string;
  ram: string;
  storage: string;
  network: string;
  os: string;
  camera: string;
  battery: string;
  logo: LogoConfig;
}

interface DesignStore extends WithSyncControls {
  // State
  activeDesign: DesignConfiguration;
  designHistory: DesignConfiguration[];
  
  // Actions
  updateField: (field: keyof DesignConfiguration, value: any) => void;
  updateComponent: (category: string, value: string) => void;
  batchUpdate: (updates: Partial<DesignConfiguration>) => void;
  loadConfiguration: (config: DesignConfiguration) => void;
  reset: () => void;
  saveToHistory: () => void;
  loadFromHistory: (index: number) => void;
  
  // Convenience setters
  setName: (name: string) => void;
  setPrice: (price: number) => void;
  setColor: (color: string) => void;
  setMaterial: (material: string) => void;
  setButtonColor: (buttonColor: string) => void;
  setHasButtons: (hasButtons: boolean) => void;
  setScreenBorderColor: (screenBorderColor: string) => void;
  setCameraStyle: (cameraStyle: string) => void;
  setCameraColor: (cameraColor: string) => void;
  setCameraPosition: (cameraPosition: { x: number; y: number }) => void;
  setButtonPositions: (buttonPositions: { volume: number; power: number }) => void;
  setPhoneSize: (phoneSize: { width: number; height: number; depth: number }) => void;
  setScreenSize: (screenSize: { width: number; height: number }) => void;
  setCornerRadius: (cornerRadius: number) => void;
  setCpu: (cpu: string) => void;
  setGpu: (gpu: string) => void;
  setRam: (ram: string) => void;
  setStorage: (storage: string) => void;
  setNetwork: (network: string) => void;
  setOs: (os: string) => void;
  setCamera: (camera: string) => void;
  setBattery: (battery: string) => void;
  setLogo: (logo: LogoConfig) => void;
}

const initialState: DesignConfiguration = {
  name: 'Custom Phone',
  price: 999,
  color: '#ffffff',
  material: 'plastic',
  buttonColor: 'match',
  hasButtons: true,
  screenBorderColor: 'match',
  cameraStyle: 'pill',
  cameraColor: '#000000',
  cameraPosition: { x: 0, y: 0.7 },
  buttonPositions: { volume: 0, power: 0 },
  phoneSize: { width: 2.5, height: 5, depth: 0.3 },
  screenSize: { width: 90, height: 95 },
  cornerRadius: 0.2,
  cpu: 'quantum-x',
  gpu: 'nexus',
  ram: '8gb',
  storage: '128gb',
  network: '5g-mmwave',
  os: 'quantum-os',
  camera: 'ultra-108',
  battery: 'ultra',
  logo: {
    type: 'none',
    scale: 0.2,
    position: { x: 0, y: 0 },
    rotation: 0,
  },
};

// Custom sync configuration for design store
const designSyncConfig = {
  routing: {
    rules: [
      // Design configuration is moderate size - keep in localStorage for quick access
      {
        match: (path) => path === 'activeDesign',
        target: 'localStorage' as const,
        priority: 100
      },
      // Design history can get large - store in IndexedDB
      {
        match: (path) => path === 'designHistory',
        target: 'indexedDB' as const,
        priority: 90
      }
    ],
    defaultTarget: 'localStorage' as const
  },
  behavior: {
    debounceDelay: 500, // 500ms debounce for design updates
    enableAutoSync: true,
    syncOnReconnect: true
  }
};

export const useDesignStore = create<DesignStore>()(
  conditionalDevtools(
    createSyncMiddleware(
      'designStore',
      designSyncConfig
    )(
      (set, get) => ({
        // State
        activeDesign: initialState,
        designHistory: [],
        
        // Actions
        updateField: (field, value) =>
          set(
            state => ({
              activeDesign: {
                ...state.activeDesign,
                [field]: value,
              },
            }),
            false,
            `updateField:${field}`
          ),
        
        updateComponent: (category, value) =>
          set(
            state => {
              const componentFieldMap: Record<string, keyof DesignConfiguration> = {
                cpu: 'cpu',
                gpu: 'gpu',
                ram: 'ram',
                storage: 'storage',
                network: 'network',
                os: 'os',
                camera: 'camera',
                battery: 'battery',
              };
              
              const field = componentFieldMap[category];
              if (!field) return state;
              
              return {
                activeDesign: {
                  ...state.activeDesign,
                  [field]: value,
                },
              };
            },
            false,
            'updateComponent'
          ),
        
        batchUpdate: (updates) =>
          set(
            state => ({
              activeDesign: {
                ...state.activeDesign,
                ...updates,
              },
            }),
            false,
            'batchUpdate'
          ),
        
        loadConfiguration: (config) =>
          set(
            { activeDesign: config },
            false,
            'loadConfiguration'
          ),
        
        reset: () =>
          set(
            { activeDesign: initialState },
            false,
            'resetDesign'
          ),
        
        saveToHistory: () =>
          set(
            state => ({
              designHistory: [...state.designHistory, state.activeDesign],
            }),
            false,
            'saveToHistory'
          ),
        
        loadFromHistory: (index) =>
          set(
            state => {
              const design = state.designHistory[index];
              if (!design) return state;
              return { activeDesign: design };
            },
            false,
            'loadFromHistory'
          ),
        
        // Convenience setters
        setName: (name) => get().updateField('name', name),
        setPrice: (price) => get().updateField('price', price),
        setColor: (color) => get().updateField('color', color),
        setMaterial: (material) => get().updateField('material', material),
        setButtonColor: (buttonColor) => get().updateField('buttonColor', buttonColor),
        setHasButtons: (hasButtons) => get().updateField('hasButtons', hasButtons),
        setScreenBorderColor: (screenBorderColor) => get().updateField('screenBorderColor', screenBorderColor),
        setCameraStyle: (cameraStyle) => get().updateField('cameraStyle', cameraStyle),
        setCameraColor: (cameraColor) => get().updateField('cameraColor', cameraColor),
        setCameraPosition: (cameraPosition) => get().updateField('cameraPosition', cameraPosition),
        setButtonPositions: (buttonPositions) => get().updateField('buttonPositions', buttonPositions),
        setPhoneSize: (phoneSize) => get().updateField('phoneSize', phoneSize),
        setScreenSize: (screenSize) => get().updateField('screenSize', screenSize),
        setCornerRadius: (cornerRadius) => get().updateField('cornerRadius', cornerRadius),
        setCpu: (cpu) => get().updateField('cpu', cpu),
        setGpu: (gpu) => get().updateField('gpu', gpu),
        setRam: (ram) => get().updateField('ram', ram),
        setStorage: (storage) => get().updateField('storage', storage),
        setNetwork: (network) => get().updateField('network', network),
        setOs: (os) => get().updateField('os', os),
        setCamera: (camera) => get().updateField('camera', camera),
        setBattery: (battery) => get().updateField('battery', battery),
        setLogo: (logo) => get().updateField('logo', logo),
      })
    ),
    {
      name: 'design-store',
    }
  )
);

// Selectors
export const selectActiveDesign = (state: DesignStore) => state.activeDesign || initialState;
export const selectDesignHistory = (state: DesignStore) => state.designHistory;
export const selectSelectedComponents = (state: DesignStore) => {
  if (!state.activeDesign) {
    // Return default components if activeDesign is not initialized
    return {
      os: initialState.os,
      cpu: initialState.cpu,
      gpu: initialState.gpu,
      ram: initialState.ram,
      storage: initialState.storage,
      network: initialState.network,
      camera: initialState.camera,
      battery: initialState.battery,
    };
  }
  
  return {
    os: state.activeDesign.os,
    cpu: state.activeDesign.cpu,
    gpu: state.activeDesign.gpu,
    ram: state.activeDesign.ram,
    storage: state.activeDesign.storage,
    network: state.activeDesign.network,
    camera: state.activeDesign.camera,
    battery: state.activeDesign.battery,
  };
};

// Computed selectors that need additional data
export const createFilteredComponentsSelector = (
  gameDate?: { year: number; month: number; day: number },
  researchUnlocks?: ResearchUnlock[]
) => {
  // First, filter by release date if gameDate is provided
  let dateFilteredComponents: ComponentCategory[] = baseComponents;
  
  if (gameDate) {
    // Filter components by release date
    dateFilteredComponents = baseComponents.map(category => {
      const availableComponents = category.options.filter(option => {
        if (!option.releaseDate) return true;
        
        const currentDate = new Date(gameDate.year, gameDate.month - 1, gameDate.day);
        const releaseDate = new Date(option.releaseDate.year, option.releaseDate.month - 1, option.releaseDate.day);
        
        return currentDate >= releaseDate;
      });
      
      return {
        ...category,
        options: availableComponents
      };
    });
  }
  
  // Then apply research filtering on top of date filtering
  if (!researchUnlocks) return dateFilteredComponents;
  return ResearchIntegrationService.filterComponentsByResearch(dateFilteredComponents, researchUnlocks);
};

export const createFilteredMaterialsSelector = (
  researchUnlocks?: ResearchUnlock[],
  completedResearch?: string[]
) => {
  if (!researchUnlocks) {
    // Return only base materials if no research
    return baseMaterialOptions.filter(mat => !mat.requiresResearch);
  }
  
  const completedResearchIds = completedResearch || [];
  
  return baseMaterialOptions.filter(mat => {
    if (!mat.requiresResearch) return true;
    // Check if the required research is completed
    return completedResearchIds.includes(mat.requiresResearch);
  });
};

// Helper to get default components based on game date
export const getDefaultDesignConfiguration = (
  gameDate?: { year: number; month: number; day: number },
  completedResearch?: string[]
): Partial<DesignConfiguration> => {
  if (!gameDate) return {};
  const defaultComponents = getDefaultComponents(gameDate, completedResearch);
  return {
    ...initialState,
    ...defaultComponents,
  };
};